// Evästeiden ja tallennusavainten siivous hylkäyksen ja peruutuksen jälkeen
// (V2-T8).
//
// Kun kävijä hylkää kategorian tai peruu aiemman suostumuksensa, kategorian
// evästeet on poistettava — pelkkä tagin estäminen ei riitä, koska aiemmin
// asetetut evästeet jäisivät kävijän laitteelle. Poistettavien nimet tulevat
// AINA sivuston konfiguraatiosta (kategorioiden evästelistoista), ei
// kovakoodatusta listasta: asiakas näkee bannerissa saman listan, jonka
// perusteella siivous tehdään.
//
// Rajoitteet (dokumentoitu docs/tietosuoja/rajoitteet.md):
// - HttpOnly-evästeitä ei voi poistaa selaimen JavaScriptistä. Ne poistuvat
//   vain palvelimen asettamalla vastauksella.
// - Kolmannen osapuolen domainille asetettuja evästeitä ei voi poistaa toisen
//   domainin sivulta (esim. doubleclick.net-eväste).
// - Evästeen voi poistaa vain, jos sen polku ja domain osataan arvata; siksi
//   yritetään useaa polku- ja domainyhdistelmää.

import type { Choices, CmpConfig } from './types';

/** Mitä siivous poisti (lokitusta ja testejä varten). */
export interface CleanupResult {
  cookies: string[];
  localStorageKeys: string[];
}

/** CMP:n omat avaimet: näitä ei koskaan poisteta (todistusketju, esto). */
const PROTECTED_PREFIX = 'cmp_';

/**
 * Muuntaa konfiguraation evästenimen vertailufunktioksi. Nimessä voi olla
 * `*`-jokerimerkkejä (skanneri kirjaa esim. `_ga_*`), muut merkit vertaillaan
 * sellaisenaan.
 */
export function nameMatcher(pattern: string): (name: string) => boolean {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  const re = new RegExp('^' + escaped + '$');
  return function (name: string) {
    return re.test(name);
  };
}

/** Kategoriat, joita kävijä ei ole hyväksynyt (välttämättömät ohitetaan). */
export function deniedCategories(config: CmpConfig, choices: Choices) {
  return (config.categories || []).filter(function (cat) {
    if (cat.is_required) return false;
    return !choices[cat.slug];
  });
}

/**
 * Siivouslista: hylättyjen kategorioiden evästenimet ja localStorage-avaimet
 * konfiguraatiosta. Tyyppi `localStorage`/`local_storage`/`session_storage`
 * ohjaa avaimen oikeaan listaan; muut käsitellään evästeinä.
 */
export function cleanupTargets(config: CmpConfig, choices: Choices) {
  const cookies: string[] = [];
  const storage: string[] = [];
  deniedCategories(config, choices).forEach(function (cat) {
    (cat.cookies || []).forEach(function (ck) {
      if (!ck.name) return;
      if (ck.name.indexOf(PROTECTED_PREFIX) === 0) return;
      const type = (ck.type || '').toLowerCase();
      const list = type.indexOf('storage') !== -1 ? storage : cookies;
      if (list.indexOf(ck.name) === -1) list.push(ck.name);
    });
  });
  return { cookies, storage };
}

/** Selaimen tämänhetkiset evästenimet. */
export function currentCookieNames(): string[] {
  const names: string[] = [];
  const raw = typeof document !== 'undefined' ? document.cookie : '';
  if (!raw) return names;
  raw.split(';').forEach(function (part) {
    const eq = part.indexOf('=');
    const name = (eq === -1 ? part : part.substring(0, eq)).trim();
    if (name) names.push(name);
  });
  return names;
}

/** Domainvaihtoehdot, joilla eväste on voitu asettaa. */
export function domainCandidates(hostname: string): string[] {
  const out: string[] = [''];
  if (!hostname) return out;
  const parts = hostname.split('.');
  // localhost tai IP-osoite: vain host itse
  if (parts.length < 2) {
    out.push(hostname);
    return out;
  }
  for (let i = 0; i <= parts.length - 2; i++) {
    const d = parts.slice(i).join('.');
    out.push(d);
    out.push('.' + d);
  }
  return out;
}

/** Polkuvaihtoehdot, joilla eväste on voitu asettaa. */
export function pathCandidates(pathname: string): string[] {
  const out = ['/'];
  if (!pathname || pathname === '/') return out;
  const segments = pathname.split('/').filter(Boolean);
  let acc = '';
  for (let i = 0; i < segments.length; i++) {
    acc += '/' + segments[i];
    out.push(acc);
  }
  return out;
}

/**
 * Poistaa evästeen kaikilla uskottavilla polku- ja domainyhdistelmillä.
 * Palauttaa true, jos eväste ei enää näy document.cookiessa.
 */
export function deleteCookie(name: string, hostname: string, pathname: string): boolean {
  const expired = '=; expires=Thu, 01 Jan 1970 00:00:01 GMT';
  pathCandidates(pathname).forEach(function (path) {
    domainCandidates(hostname).forEach(function (domain) {
      document.cookie = name + expired + '; path=' + path + (domain ? '; domain=' + domain : '');
    });
  });
  return currentCookieNames().indexOf(name) === -1;
}

/**
 * Siivoaa hylättyjen kategorioiden evästeet ja tallennusavaimet. Ajetaan
 * jokaisen suostumuksen soveltamisen yhteydessä, joten myöhemmin ilmestyvä
 * eväste poistuu seuraavalla sivulatauksella.
 */
export function cleanupDeniedStorage(config: CmpConfig | null, choices: Choices): CleanupResult {
  const result: CleanupResult = { cookies: [], localStorageKeys: [] };
  if (!config) return result;

  const targets = cleanupTargets(config, choices);
  if (targets.cookies.length === 0 && targets.storage.length === 0) return result;

  const cookieMatchers = targets.cookies.map(nameMatcher);
  const storageMatchers = targets.storage.map(nameMatcher);

  // Evästeet: poistetaan vain ne, jotka tosiasiassa ovat selaimessa
  if (cookieMatchers.length > 0) {
    const hostname = location.hostname;
    const pathname = location.pathname;
    currentCookieNames().forEach(function (name) {
      if (name.indexOf(PROTECTED_PREFIX) === 0) return;
      const osuma = cookieMatchers.some(function (m) { return m(name); });
      if (!osuma) return;
      deleteCookie(name, hostname, pathname);
      if (result.cookies.indexOf(name) === -1) result.cookies.push(name);
    });
  }

  // localStorage ja sessionStorage
  if (storageMatchers.length > 0) {
    [safeStorage('localStorage'), safeStorage('sessionStorage')].forEach(function (store) {
      if (!store) return;
      const keys: string[] = [];
      try {
        for (let i = 0; i < store.length; i++) {
          const k = store.key(i);
          if (k) keys.push(k);
        }
      } catch { return; }
      keys.forEach(function (k) {
        if (k.indexOf(PROTECTED_PREFIX) === 0) return;
        if (!storageMatchers.some(function (m) { return m(k); })) return;
        try { store.removeItem(k); } catch { return; }
        if (result.localStorageKeys.indexOf(k) === -1) result.localStorageKeys.push(k);
      });
    });
  }

  return result;
}

function safeStorage(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    return window[kind];
  } catch {
    return null;
  }
}
